'use client';

import { useState } from 'react';
import { Image } from '@/components/ui';
import { Tables } from '@/types/supabase';
import { playerModelV2 } from '@/models/playerModelV2';
import { useFormatName } from '@/contexts/NameFormattingContext';

interface PlayerAvatarProps {
  name?: string;
  profilePhoto?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'compact';
  className?: string;
  color?: string;
  player?: Tables<'player'>;
}

export default function PlayerAvatar({
  name = '',
  profilePhoto = null,
  size = 'md',
  className = '',
  color,
  player,
}: PlayerAvatarProps) {
  const formatName = useFormatName();
  // Use player data if available
  const playerName = player ? formatName(player.first_name, player.last_name) : name;
  const playerPhoto = player?.profile_photo || profilePhoto;

  // Get initials from name
  const getInitials = (fullName: string) => {
    if (player) {
      return playerModelV2.getInitials(player.first_name, player.last_name, player.username);
    }
    const names = fullName.trim().split(' ');
    if (names.length === 1) {
      return names[0]?.[0]?.toUpperCase() || '?';
    }
    return (names[0]?.[0] + names[names.length - 1]?.[0])?.toUpperCase() || '?';
  };

  // Size classes
  const sizeClasses = {
    sm: 'h-8 w-8 text-sm',
    md: 'h-12 w-12 text-base',
    lg: 'h-16 w-16 text-lg',
    xl: 'h-24 w-24 text-2xl',
    compact: 'h-9 w-9 text-xs',
  };

  const baseClasses = `flex items-center justify-center rounded-full font-medium overflow-hidden relative ${sizeClasses[size]} ${className}`;

  // Track image-load failure in React state so we can re-render the
  // initials branch cleanly. The previous implementation mutated
  // `parent.innerHTML` inside an onError, which raced React's own
  // reconciliation when the `<Image>` re-rendered as null.
  const [imageFailed, setImageFailed] = useState(false);

  // Fallback styling when no photo OR image failed.
  const backgroundColor = color || 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  if (!playerPhoto || imageFailed) {
    return <div className={`${baseClasses} ${backgroundColor}`}>{getInitials(playerName)}</div>;
  }

  return (
    <div className={baseClasses}>
      <Image
        src={playerPhoto}
        alt={playerName || 'Player avatar'}
        width={128}
        height={128}
        className="absolute inset-0 h-full w-full object-cover"
        sizes="128px"
        onError={() => setImageFailed(true)}
      />
    </div>
  );
}
